/**
 * React Query hooks + types for the IT > Adoption tool (/it/adoption).
 *
 * How much of what we built is actually being used: per-tool usage against
 * the people or stores who hold that tool. Every endpoint lives under
 * /v1/adoption/* on the shared API (spec: parallel build in the api repo
 * against this exact contract).
 *
 *   GET /v1/adoption/summary?days=&include_it=              five pillar tiles
 *   GET /v1/adoption/tools?days=&include_it=&surface=&status=   the tool grid
 *   GET /v1/adoption/tools/{tool_key}/users?days=&include_it=   drill: who used it
 *   GET /v1/adoption/users/{principal_ref}?days=                drill: one person/store
 *   GET /v1/adoption/settings                                   targets + launch dates
 *   PUT /v1/adoption/settings                                   partial update
 *
 * Graceful degradation (per COCKPIT.md 3, Connect/ItemSales pattern): the
 * backend ships in parallel. A 404 (route not deployed) or 503 (feature not
 * configured) is a quiet "not available yet" state, never a crash. Every
 * query below uses retry:false so a missing route surfaces fast; callers use
 * isNotAvailable(error) to detect it, and safeArray/safeNum guard against a
 * response whose shape does not fully match what is documented here yet.
 */

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from '@tanstack/react-query';
import { apiGet, apiPut, type ApiError } from './api';
import { API_BASE } from './apiBase';

export const ADOPTION_API_BASE = `${API_BASE}/v1/adoption`;

// --------------------------------------------------------------------------
// RBAC + degrade helpers
// --------------------------------------------------------------------------

export function hasItAdoption(perms: string[] | undefined, superAdmin = false): boolean {
  if (superAdmin) return true;
  return !!perms?.includes('it.adoption');
}

/** A 404 (route not yet deployed) or 503 (feature not configured) means the
 * backend for this slice is not live yet. Pages render a quiet placeholder. */
export function isNotAvailable(error: unknown): boolean {
  const status = (error as ApiError | null)?.status;
  return status === 404 || status === 503;
}

function safeArray<T>(v: unknown): T[] {
  return Array.isArray(v) ? (v as T[]) : [];
}

// --------------------------------------------------------------------------
// Shared types
// --------------------------------------------------------------------------

export type AdoptionSurface =
  | 'cockpit'
  | 'sweetspot'
  | 'connect'
  | 'teams'
  | 'helpdesk'
  | 'store_tickets'
  | 'cameras';

export const ADOPTION_SURFACES: AdoptionSurface[] = [
  'helpdesk',
  'store_tickets',
  'cockpit',
  'sweetspot',
  'connect',
  'teams',
  'cameras',
];

/** Everyday names for the surfaces, used in the filter dropdown and the grid. */
// Steven's naming pass (2026-08-27): both service desks are Jira Service
// Management, so their surface reads JSM (the filter dropdown disambiguates
// with (Corp)/(Stores)); the camera pages run Frigate; Teams is just Teams
// (AI Assistant is the SECTION of the Ask Amp rows, not their surface).
export const SURFACE_LABELS: Record<AdoptionSurface, string> = {
  helpdesk: 'JSM',
  store_tickets: 'JSM',
  cockpit: 'Cockpit',
  sweetspot: 'Sweet Spot',
  connect: 'Connect',
  teams: 'Teams',
  cameras: 'Frigate',
};

/** Filter-dropdown labels: JSM appears twice as a surface, so the options disambiguate. */
export const SURFACE_FILTER_LABELS: Record<AdoptionSurface, string> = {
  ...SURFACE_LABELS,
  helpdesk: 'JSM (Corp)',
  store_tickets: 'JSM (Stores)',
};

export function surfaceLabel(surface: string): string {
  return SURFACE_LABELS[surface as AdoptionSurface] ?? surface;
}

// --------------------------------------------------------------------------
// Range: the on-screen choice maps to a plain days count, which is all the
// api needs (?days=7|14|30|90|365). Kept as a separate key/days pair rather
// than sending the label so the URL param (?range=1m) reads cleanly and a
// future range option does not have to renumber every days value.
// --------------------------------------------------------------------------

export type AdoptionRangeKey = '7d' | '14d' | '1m' | '1q' | '1y';
export type AdoptionRangeDays = 7 | 14 | 30 | 90 | 365;

export const ADOPTION_RANGE_OPTIONS: Array<{ value: AdoptionRangeKey; label: string; days: AdoptionRangeDays }> = [
  { value: '7d', label: 'Last 7 days', days: 7 },
  { value: '14d', label: 'Last 14 days', days: 14 },
  { value: '1m', label: 'Last month', days: 30 },
  { value: '1q', label: 'Last quarter', days: 90 },
  { value: '1y', label: 'Last year', days: 365 },
];

export const DEFAULT_ADOPTION_RANGE: AdoptionRangeKey = '1m';

/** URL -> days. Falls back to the default range for a missing or unknown key,
 * so a hand-edited or stale URL never sends an invalid `days` value. */
export function rangeKeyToDays(key: string | null | undefined): AdoptionRangeDays {
  const found = ADOPTION_RANGE_OPTIONS.find((o) => o.value === key);
  if (found) return found.days;
  return ADOPTION_RANGE_OPTIONS.find((o) => o.value === DEFAULT_ADOPTION_RANGE)!.days;
}

export function isValidRangeKey(key: string | null | undefined): key is AdoptionRangeKey {
  return ADOPTION_RANGE_OPTIONS.some((o) => o.value === key);
}

// --------------------------------------------------------------------------
// Summary (pillar tiles)
// --------------------------------------------------------------------------

export type AdoptionSeriesPoint = { week_start: string; used: number };

export type AdoptionPillar = {
  key: string;
  label: string;
  used: number | null;
  population: number | null;
  pct: number | null;
  target: number | null;
  expected_from: string | null;
  on_target: boolean | null;
  series: AdoptionSeriesPoint[];
};

export type AdoptionSummaryResponse = {
  history_from: string | null;
  pillars: AdoptionPillar[];
};

export function useAdoptionSummary(days: AdoptionRangeDays, includeIt: boolean) {
  return useQuery<AdoptionSummaryResponse, Error>({
    queryKey: ['adoption', 'summary', days, includeIt],
    queryFn: () =>
      apiGet<AdoptionSummaryResponse>(
        `${ADOPTION_API_BASE}/summary?days=${days}&include_it=${includeIt ? 1 : 0}`,
      ),
    staleTime: 30_000,
    retry: false,
  });
}

/** A date string ("YYYY-MM-DD") strictly after `today` (defaults to now). Pure
 * and takes `today` as a parameter so it is unit-testable without mocking the
 * clock. */
export function isFutureDate(dateStr: string, today: Date = new Date()): boolean {
  const d = new Date(`${dateStr}T00:00:00`);
  if (Number.isNaN(d.getTime())) return false;
  const t = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  return d.getTime() > t.getTime();
}

export type PillarStatusTone = 'good' | 'bad' | 'warn';
export type PillarStatus = { label: string; tone: PillarStatusTone };

/**
 * The status pill shown on a pillar tile. Checked in this order:
 * 1. No usage history at all for this pillar yet -> "History starts {date}".
 * 2. The tool has not launched yet (expected_from is in the future) -> "Launches {date}".
 * 3. Meeting its target -> "On target".
 * 4. Otherwise -> "Under".
 */
export function pillarStatus(
  pillar: Pick<AdoptionPillar, 'used' | 'expected_from' | 'on_target'>,
  historyFrom: string | null,
  today: Date = new Date(),
): PillarStatus {
  if (pillar.used === null || pillar.used === undefined) {
    return { label: historyFrom ? `History starts ${historyFrom}` : 'No history yet', tone: 'warn' };
  }
  if (pillar.expected_from && isFutureDate(pillar.expected_from, today)) {
    return { label: `Launches ${pillar.expected_from}`, tone: 'warn' };
  }
  if (pillar.on_target) {
    return { label: 'On target', tone: 'good' };
  }
  return { label: 'Under', tone: 'bad' };
}

// --------------------------------------------------------------------------
// Tools grid
// --------------------------------------------------------------------------

export type AdoptionToolUnit = 'people' | 'stores';

export type AdoptionTool = {
  tool_key: string;
  label: string;
  surface: AdoptionSurface;
  /** Sidebar section the tool lives under ('Stores / Daily ops', 'Commerce'). Null on rows created before the api carried it. */
  section: string | null;
  unit: AdoptionToolUnit;
  expected_from: string | null;
  used: number | null;
  access: number | null;
  pct: number | null;
  last_used_at: string | null;
};

export type AdoptionToolsResponse = {
  history_from: string | null;
  tools: AdoptionTool[];
};

export type AdoptionStatusFilter = 'all' | 'used' | 'never_used' | 'awaiting_launch';

export const ADOPTION_STATUS_OPTIONS: Array<{ value: AdoptionStatusFilter; label: string }> = [
  { value: 'all', label: 'All' },
  { value: 'used', label: 'Used' },
  { value: 'never_used', label: 'Never used' },
  { value: 'awaiting_launch', label: 'Awaiting launch' },
];

export type AdoptionToolsParams = {
  days: AdoptionRangeDays;
  includeIt: boolean;
  surface: AdoptionSurface | 'all';
  status: AdoptionStatusFilter;
};

function toolsQueryString(params: AdoptionToolsParams): string {
  const qs = new URLSearchParams();
  qs.set('days', String(params.days));
  qs.set('include_it', params.includeIt ? '1' : '0');
  if (params.surface !== 'all') qs.set('surface', params.surface);
  if (params.status !== 'all') qs.set('status', params.status);
  return qs.toString();
}

export function useAdoptionTools(params: AdoptionToolsParams): UseQueryResult<AdoptionToolsResponse, Error> {
  const qs = toolsQueryString(params);
  return useQuery<AdoptionToolsResponse, Error>({
    queryKey: ['adoption', 'tools', qs],
    queryFn: () => apiGet<AdoptionToolsResponse>(`${ADOPTION_API_BASE}/tools?${qs}`),
    staleTime: 30_000,
    retry: false,
  });
}

/** True for a tool that has not launched yet (no data source, no expected date
 * has arrived). Rows in this state are muted and excluded from footer totals. */
export function isAwaitingLaunch(tool: Pick<AdoptionTool, 'expected_from'>, today: Date = new Date()): boolean {
  return !!tool.expected_from && isFutureDate(tool.expected_from, today);
}

// --------------------------------------------------------------------------
// Drill-down: who used a tool
// --------------------------------------------------------------------------

export type AdoptionToolUser = {
  principal_ref: string;
  name: string;
  kind: string; // 'person' | 'store' (backend-defined; treat unknown values as person)
  is_it: boolean;
  tools_used: number;
  last_used_at: string | null;
  events: number;
};

export type AdoptionToolUsersResponse = { users: AdoptionToolUser[] };

export function useAdoptionToolUsers(
  toolKey: string | null,
  days: AdoptionRangeDays,
  includeIt: boolean,
) {
  return useQuery<AdoptionToolUsersResponse, Error>({
    queryKey: ['adoption', 'tool-users', toolKey, days, includeIt],
    queryFn: () =>
      apiGet<AdoptionToolUsersResponse>(
        `${ADOPTION_API_BASE}/tools/${encodeURIComponent(toolKey ?? '')}/users?days=${days}&include_it=${includeIt ? 1 : 0}`,
      ),
    enabled: !!toolKey,
    staleTime: 30_000,
    retry: false,
  });
}

// --------------------------------------------------------------------------
// Drill-down: one person or store, across every tool they hold
// --------------------------------------------------------------------------

export type AdoptionUserToolRow = {
  tool_key: string;
  label: string;
  surface: AdoptionSurface;
  last_used_at: string | null;
  events: number;
};

export type AdoptionUserDetailResponse = {
  user: { principal_ref: string; name: string; kind: string; is_it: boolean };
  tools: AdoptionUserToolRow[];
};

export function useAdoptionUserDetail(principalRef: string | null, days: AdoptionRangeDays) {
  return useQuery<AdoptionUserDetailResponse, Error>({
    queryKey: ['adoption', 'user', principalRef, days],
    queryFn: () =>
      apiGet<AdoptionUserDetailResponse>(
        `${ADOPTION_API_BASE}/users/${encodeURIComponent(principalRef ?? '')}?days=${days}`,
      ),
    enabled: !!principalRef,
    staleTime: 30_000,
    retry: false,
  });
}

// --------------------------------------------------------------------------
// Settings: targets, per-tool launch dates, IT-member list
// --------------------------------------------------------------------------

export type AdoptionSettingsTarget = {
  line_key: string;
  label: string;
  target: number;
  expected_from: string | null;
};

export type AdoptionSettingsTool = {
  tool_key: string;
  label: string;
  /** Present since the 2026-08-27 naming pass; older api builds omit them. */
  surface?: string;
  section?: string | null;
  expected_from: string | null;
  /** Microsoft 365 group that controls access, when one does (read-only here). */
  entra_group_id?: string | null;
};

export type AdoptionSettingsItMember = { email: string; effective_from: string | null };

export type AdoptionSettingsResponse = {
  targets: AdoptionSettingsTarget[];
  tools: AdoptionSettingsTool[];
  it_members: AdoptionSettingsItMember[];
};

export function useAdoptionSettings(enabled: boolean) {
  return useQuery<AdoptionSettingsResponse, Error>({
    queryKey: ['adoption', 'settings'],
    queryFn: () => apiGet<AdoptionSettingsResponse>(`${ADOPTION_API_BASE}/settings`),
    enabled,
    staleTime: 10_000,
    retry: false,
  });
}

/**
 * Browser download of the tools grid as XLSX, with the current filters.
 * Mirrors downloadTelemetryXlsx (useTelemetry.ts).
 */
export async function downloadAdoptionXlsx(params: AdoptionToolsParams): Promise<void> {
  const qs = toolsQueryString(params);
  const res = await fetch(`${ADOPTION_API_BASE}/export?${qs}`, { credentials: 'include' });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`Export failed: ${res.status} ${text}`);
  }
  const blob = await res.blob();
  const dispo = res.headers.get('content-disposition') ?? '';
  const m = dispo.match(/filename="([^"]+)"/);
  const filename = m?.[1] ?? 'adoption.xlsx';
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = objectUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(objectUrl);
}

export type AdoptionSettingsPutInput = {
  targets?: Array<{ line_key: string; target?: number; expected_from?: string | null }>;
  tools?: Array<{ tool_key: string; expected_from?: string | null }>;
  /** add entries carry the effective date the api schema requires. */
  it_members?: { add?: Array<{ email: string; effective_from: string }>; remove?: string[] };
};

export function useUpdateAdoptionSettings() {
  const qc = useQueryClient();
  return useMutation<AdoptionSettingsResponse, Error, AdoptionSettingsPutInput>({
    mutationFn: (body) => apiPut<AdoptionSettingsResponse>(`${ADOPTION_API_BASE}/settings`, body),
    onSuccess: (data) => {
      qc.setQueryData(['adoption', 'settings'], data);
      void qc.invalidateQueries({ queryKey: ['adoption', 'summary'] });
      void qc.invalidateQueries({ queryKey: ['adoption', 'tools'] });
    },
  });
}

export { safeArray };
